import api from '../services/api';
import { getSiteId } from '../siteId/siteId';

/*
 * The storefront slice of OrderItemV2RestController and the whole of RegularOrderItemV2RestController -
 * everything an order detail page needs that hangs off an order *item* rather than the order.
 *
 * Two prefixes, because the server splits them: reorder, stock and personalization live on
 * /orderItems/v2 alongside a large admin-only surface, while the consumer file and proof operations
 * have their own controller at /regularOrderItems/v2 which is storefront-only end to end. Both are
 * reached by order item id and guarded by StandardOrderItemAccessValidator, so they belong to this
 * domain rather than to a service of their own.
 *
 * Not reachable from here and deliberately absent: everything else on OrderItemV2RestController
 * (production status, prepress, job name/description, cancel, quantity adjustment, proof status as
 * *admin* sets it) is SUPPLIER_ADMIN/SITE_ADMIN, as are the consumerProofFiles write operations -
 * RegularOrderItemV2RestServiceAdaptor gates those with standardSupplierSiteAdminOnlyAccessValidator
 * even though the controller class is annotated STOREFRONT.
 */

const ORDER_ITEMS_V2_URL = '/orderItems/v2';
const REGULAR_ORDER_ITEMS_V2_URL = '/regularOrderItems/v2';

const orderedItemService = {
    getReorderUrl,
    getStockItems,
    getPrintFiles,
    addPrintFile,
    activatePrintFile,
    deactivatePrintFile,
    patchPrintFile,
    getProofFiles,
    getProofVisual,
    approveProof,
    declineProof,
    getTemplatePreview,
    startTemplateEdit,
    saveTemplateEdit,
};

export default orderedItemService;

/*
 * isStock is not a hint, it selects between two different implementations in
 * OrderItemProvider#generateReorderURL: a stock item returns its offering's bare urlPattern, a POD item
 * returns /offering{shareUrlPattern} with the reorder parameters attached. Pass the wrong one and the
 * call succeeds and hands back a plain product page, so the API layer derives it from the item rather
 * than letting a caller supply it.
 */

function getReorderUrl (orderItemId, reuseFiles, isStock) {
    const requestConfig = {
        url: `${ORDER_ITEMS_V2_URL}/${orderItemId}/reorderURL`,
        method: 'GET',
        params: {
            siteId: getSiteId(),
            reuseFiles,
            isStock,
        },
    };

    return api(requestConfig);
}

/*
 * Not the stock lines the items endpoint is missing - it is barely missing any. Read
 * RegularOrderItemPredicates#buildPredicateForOrders: /orders/v2/{orderIds}/items excludes an item only
 * when it is a product set *child* and stock, so every standalone stock line is already in getItems.
 * Those set children are the one completeness reason to call this.
 *
 * The other is inventory. RegularOrderItemDTOMapperImpl#mapRegularOrderItem never maps available
 * quantity - it has extractStockAvailableQty, but only calls it when building the project-line
 * OrderItemDTO - so RegularOrderItemDTO carries the variant ids and a choices string and nothing live.
 * findStockItemsByOrderId resolves those ids to the variant itself, availableInventory included, which
 * makes this the only route to a stock level anywhere in the SDK. A detail lookup joined to the items by
 * id, then, not a second list to append to them.
 */

function getStockItems (orderId) {
    const requestConfig = {
        url: `${ORDER_ITEMS_V2_URL}/orders/${orderId}/stockList`,
        method: 'GET',
        params: {
            siteId: getSiteId(),
        },
    };

    return api(requestConfig);
}

function getPrintFiles (orderItemId) {
    const requestConfig = {
        url: `${REGULAR_ORDER_ITEMS_V2_URL}/${orderItemId}/consumerPrintFiles`,
        method: 'GET',
        params: {
            siteId: getSiteId(),
        },
    };

    return api(requestConfig);
}

/*
 * The bytes are already in S3 by the time this runs - RegularOrderItemV2Provider#createPrintFile calls
 * the same OrderItemConsumerPrintFileService#createFromStorefrontStagedFileReservation the cart upload
 * path uses, so a reservation staged through fileUploadService is the right input here.
 */

function addPrintFile (orderItemId, stagedFileReservationName) {
    const requestConfig = {
        url: `${REGULAR_ORDER_ITEMS_V2_URL}/${orderItemId}/consumerPrintFiles`,
        method: 'POST',
        params: {
            siteId: getSiteId(),
        },
        data: {
            stagedFileReservationName,
        },
    };

    return api(requestConfig);
}

function activatePrintFile (orderItemId, printFileId) {
    const requestConfig = {
        url: `${REGULAR_ORDER_ITEMS_V2_URL}/${orderItemId}/consumerPrintFiles/${printFileId}/activate`,
        method: 'PUT',
        params: {
            siteId: getSiteId(),
        },
    };

    return api(requestConfig);
}

function deactivatePrintFile (orderItemId, printFileId) {
    const requestConfig = {
        url: `${REGULAR_ORDER_ITEMS_V2_URL}/${orderItemId}/consumerPrintFiles/${printFileId}/deactivate`,
        method: 'PUT',
        params: {
            siteId: getSiteId(),
        },
    };

    return api(requestConfig);
}

/*
 * OrderItemConsumerPrintFilePatchDTO exposes exactly one field, note. Anything else in the body is
 * accepted and dropped, so the API layer builds the payload rather than forwarding a caller's object.
 */

function patchPrintFile (orderItemId, printFileId, patch) {
    const requestConfig = {
        url: `${REGULAR_ORDER_ITEMS_V2_URL}/${orderItemId}/consumerPrintFiles/${printFileId}`,
        method: 'PATCH',
        params: {
            siteId: getSiteId(),
        },
        data: patch,
    };

    return api(requestConfig);
}

function getProofFiles (orderItemId) {
    const requestConfig = {
        url: `${REGULAR_ORDER_ITEMS_V2_URL}/${orderItemId}/consumerProofFiles`,
        method: 'GET',
        params: {
            siteId: getSiteId(),
        },
    };

    return api(requestConfig);
}

/*
 * A passthrough proxy for sls-job-visuals, not a Collaterate resource. JobVisualsClient swallows every
 * upstream failure - a disabled integration, a timeout, an unparseable body - into a 200 carrying
 * {status: "none", reason: "unavailable"}, so this never rejects for an unavailable visual and the
 * status in the body is the only thing worth branching on.
 */

function getProofVisual (orderItemId) {
    const requestConfig = {
        url: `${REGULAR_ORDER_ITEMS_V2_URL}/${orderItemId}/proofVisual`,
        method: 'GET',
        params: {
            siteId: getSiteId(),
        },
    };

    return api(requestConfig);
}

function approveProof (orderItemId, comment) {
    const requestConfig = {
        url: `${REGULAR_ORDER_ITEMS_V2_URL}/${orderItemId}/proofStatus/approve`,
        method: 'POST',
        params: {
            siteId: getSiteId(),
        },
        data: {
            comment,
        },
    };

    return api(requestConfig);
}

/*
 * RegularOrderItemV2Provider#setProofStatusDecline writes the log entry as
 * String.join(" ", denialReason.getName(), request.getComment()) - a null comment joins as the literal
 * "null" and is what staff then read on the order. The API layer always sends a string for that reason.
 */

function declineProof (orderItemId, denialReasonId, comment) {
    const requestConfig = {
        url: `${REGULAR_ORDER_ITEMS_V2_URL}/${orderItemId}/proofStatus/decline`,
        method: 'POST',
        params: {
            siteId: getSiteId(),
        },
        data: {
            denialReasonId,
            comment,
        },
    };

    return api(requestConfig);
}

/*
 * The ordered-item twin of projectService.getItemTemplatePreview. Same PersonalizationPreviewsDTO, same
 * maxPixelDimension and includeThumbnails parameters - the only difference is that this one addresses an
 * item that has already been ordered.
 */

function getTemplatePreview (orderItemId, params) {
    const requestConfig = {
        url: `${ORDER_ITEMS_V2_URL}/${orderItemId}/personalization/previews`,
        method: 'GET',
        params: Object.assign({ siteId: getSiteId() }, params),
    };

    return api(requestConfig);
}

function startTemplateEdit (orderItemId, startOver) {
    const requestConfig = {
        url: `${ORDER_ITEMS_V2_URL}/${orderItemId}/personalization/edit`,
        method: 'GET',
        params: {
            siteId: getSiteId(),
            startOver,
        },
    };

    return api(requestConfig);
}

function saveTemplateEdit (orderItemId, podTemplateSessionCode) {
    const requestConfig = {
        url: `${ORDER_ITEMS_V2_URL}/${orderItemId}/personalization/edit`,
        method: 'PUT',
        params: {
            siteId: getSiteId(),
        },
        data: {
            podTemplateSessionCode,
        },
    };

    return api(requestConfig);
}
